import {
	Facebook,
	Linkedin,
	Twitter,
	Instagram,
	Youtube,
	ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";
import { BrandLogo } from "./BrandLogo";
import type { NavGroup, NavItem, FooterConfig, FooterSocialLink } from "@/lib/cms";
import { NavIconRenderer } from "@/lib/nav-icons";
import { replaceCmsPlaceholders, type CMSPlaceholderReplacements } from "@/lib/cms-placeholders";
import { resolveDonateNavHref } from "@/lib/donate-nav-links";
import { NewsletterSubscribeForm } from "./NewsletterSubscribeForm";
import { useCookieConsent } from "@/context/CookieConsentContext";

/** Contact info from app settings (optional). */
interface ContactInfo {
	contactPhone?: string;
	contactEmail?: string;
	companyAddress?: string;
}

interface FooterProps {
	brandName?: string;
	brandTagline?: string;
	contact?: ContactInfo;
	/** When true, links and form are non-interactive (e.g. for CMS preview). */
	linksDisabled?: boolean;
	/** Dynamic footer config from CMS. */
	footerConfig?: FooterConfig | null;
	/** Dynamic nav groups from CMS (footer placement). */
	navGroups?: NavGroup[];
	/** Dynamic nav items from CMS (footer placement). */
	navItems?: NavItem[];
	/** If set, renders this raw HTML instead of the default React footer. */
	footerHtml?: string | null;
	/** For substituting {{phone-number}}, etc. in nav item HTML snippets. */
	placeholderReplacements?: CMSPlaceholderReplacements | null;
}

const SOCIAL_ICON: Record<FooterSocialLink['platform'], React.ReactNode> = {
	facebook: <Facebook size={20} />,
	linkedin: <Linkedin size={20} />,
	twitter: <Twitter size={20} />,
	instagram: <Instagram size={20} />,
	youtube: <Youtube size={20} />,
	tiktok: <TikTokIcon size={20} />,
};

const SOCIAL_LABEL: Record<FooterSocialLink['platform'], string> = {
	facebook: 'Facebook',
	linkedin: 'LinkedIn',
	twitter: 'Twitter / X',
	instagram: 'Instagram',
	youtube: 'YouTube',
	tiktok: 'TikTok',
};

/** Default social links when footer config has none. Order: TikTok, Facebook, LinkedIn, Instagram, YouTube. */
const DEFAULT_SOCIAL_LINKS: { url: string; platform: FooterSocialLink['platform'] }[] = [
	{ url: 'https://tiktok.com/@gsgoorg', platform: 'tiktok' },
	{ url: 'https://www.facebook.com/gsgoorg', platform: 'facebook' },
	{ url: 'https://www.linkedin.com/company/gsgo/', platform: 'linkedin' },
	{ url: 'https://www.instagram.com/gsgoorg', platform: 'instagram' },
	{ url: 'https://www.youtube.com/@gsgoorg', platform: 'youtube' },
];

/** TikTok icon (outline style) — Lucide does not provide a TikTok icon. */
function TikTokIcon({ size = 20 }: { size?: number }) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
			<path d="M9 12a4 4 0 0 0 4 4h0a4 4 0 0 0 4-4V4" />
			<path d="M17 2h-2a3 3 0 0 0-3 3v2" />
		</svg>
	);
}

export const Footer = ({
	brandName,
	brandTagline,
	contact,
	linksDisabled = false,
	footerConfig = null,
	navGroups = [],
	navItems = [],
	footerHtml = null,
	placeholderReplacements = null,
}: FooterProps) => {
	const { openPreferences } = useCookieConsent();
	// If a custom HTML override is set, render it directly instead of the React template.
	if (footerHtml && footerHtml.trim()) {
		return (
			<div>
				<div dangerouslySetInnerHTML={{ __html: footerHtml }} />
				{!linksDisabled ? (
					<div className="bg-[#1a1a1a] border-t border-gray-700 px-4 py-3 text-right">
						<button
							type="button"
							onClick={openPreferences}
							className="text-xs uppercase tracking-wide text-gray-400 hover:text-white transition-colors"
						>
							Cookie Settings
						</button>
					</div>
				) : null}
			</div>
		);
	}
	const effectiveBrandName = brandName || "GSGO";
	const effectiveTagline = brandTagline || "Global Support for Growth and Opportunity";
	const contactPhone = contact?.contactPhone?.trim();

	// Dynamic config values with fallbacks
	const showNewsletterSection = footerConfig?.showNewsletterSection === true;
	const newsletterHeading = footerConfig?.newsletterHeading || undefined;
	const newsletterDescription = footerConfig?.newsletterDescription || undefined;
	const copyrightText =
		footerConfig?.copyrightText ??
		`© ${new Date().getFullYear()} Good Samaritan Global Outreach. All Rights Reserved.`;
	const brandDescription =
		footerConfig?.brandDescription ??
		`"${effectiveTagline === "Global Support for Growth and Opportunity" ? "Saving Lives. Bridging Worlds." : effectiveTagline}"`;
	const socialLinks = footerConfig?.socialLinks ?? null; // null = use hardcoded FB/LinkedIn
	const savedFooterNavGroupIds = footerConfig?.footerNavGroupIds ?? [];

	// Build ordered footer column IDs: use saved config order, then append any footer-placement
	// groups not yet in config (e.g. newly added "Support") so new columns appear without re-saving Footer Config.
	const footerPlacementGroups = navGroups.filter((g) => g.placement === "footer");
	const effectiveFooterNavGroupIds =
		savedFooterNavGroupIds.length > 0
			? [
					...savedFooterNavGroupIds.filter((id) => footerPlacementGroups.some((g) => g.id === id)),
					...footerPlacementGroups
						.filter((g) => !savedFooterNavGroupIds.includes(g.id))
						.sort((a, b) => a.order - b.order)
						.map((g) => g.id),
				]
			: footerPlacementGroups.sort((a, b) => a.order - b.order).map((g) => g.id);

	// Build ordered footer columns from CMS nav groups
	const footerColumns: { group: NavGroup; items: NavItem[] }[] = (() => {
		if (effectiveFooterNavGroupIds.length === 0) return [];
		return effectiveFooterNavGroupIds
			.map((id) => navGroups.find((g) => g.id === id))
			.filter((g): g is NavGroup => !!g && g.isVisible)
			.map((group) => ({
				group,
				items: navItems
					.filter((it) => it.groupId === group.id && !it.parentId && it.isVisible)
					.sort((a, b) => a.order - b.order),
			}));
	})();
	const gridColumnsClass =
		footerColumns.length > 0
			? footerColumns.length >= 3
				? "md:grid-cols-4"
				: footerColumns.length === 2
					? "md:grid-cols-3"
					: "md:grid-cols-2"
			: "md:grid-cols-4";

	return (
		<footer className="bg-gray-900 text-gray-300 border-t border-gray-700 shrink-0">
			{/* Newsletter subscription band */}
			{showNewsletterSection && (
				<div className="bg-gradient-to-r from-[#030F2F] via-[#151B27] to-[#0F131D] border-b border-blue-500/40">
					<div className="max-w-7xl mx-auto px-4 py-10">
						<div className="max-w-2xl mx-auto text-center">
							<h2 className="text-xl md:text-2xl font-extrabold text-white tracking-tight mb-2">
								{newsletterHeading ?? 'Stay in the loop'}
							</h2>
							<p className="text-sm text-blue-100 mb-6">
								{newsletterDescription ?? 'Get government contracting insights, tips, and updates delivered to your inbox.'}
							</p>
							<div className={linksDisabled ? 'pointer-events-none opacity-90' : ''} aria-disabled={linksDisabled}>
								<NewsletterSubscribeForm
									source="footer_newsletter"
									heading=""
									description=""
									buttonLabel="Subscribe"
									variant="default"
								/>
							</div>
						</div>
					</div>
				</div>
			)}

			<div className="max-w-7xl mx-auto px-4 pt-6 pb-3">
				<div className={`grid grid-cols-1 gap-8 ${gridColumnsClass}`}>
					{/* Brand column */}
					<div>
						<BrandLogo
							variant="placeholder"
							label={effectiveBrandName}
							onDark
							className="mb-3 shrink-0"
							alt={effectiveBrandName}
						/>
						<p className="text-base italic text-slate-100/90 mb-4">
							{brandDescription}
						</p>
						<p className="text-sm text-gray-300">Texas Nonprofit - 501(c)(3) Pending</p>
						<p className="text-sm text-gray-300">GSGO.org</p>
						{contactPhone ? <p className="text-xs text-gray-400">{contactPhone}</p> : null}
						<p className="text-xs text-gray-400 mb-3">501(c)(3)</p>
						<div className="flex flex-wrap items-center gap-2 mb-4">
							<span className="inline-flex items-center rounded border border-white/20 px-2 py-1 text-[10px] font-semibold tracking-wider text-slate-100">SSL SECURED</span>
							<span className="inline-flex items-center rounded border border-white/20 px-2 py-1 text-[10px] font-semibold tracking-wider text-slate-100">STRIPE CERTIFIED</span>
							<span className="inline-flex items-center rounded border border-white/20 px-2 py-1 text-[10px] font-semibold tracking-wider text-slate-100">501(C)(3)</span>
						</div>
						{/* Social links — dynamic or hardcoded fallback */}
						<div className="flex items-center gap-3">
							{socialLinks !== null && socialLinks.length > 0 ? (
								socialLinks.filter((s) => s.isVisible).map((s) => (
									linksDisabled ? (
										<span key={s.id} className="text-gray-500" aria-hidden="true">{SOCIAL_ICON[s.platform]}</span>
									) : (
										<a
											key={s.id}
											href={s.url}
											target="_blank"
											rel="noopener noreferrer"
											className="text-gray-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded"
											aria-label={`${effectiveBrandName} on ${SOCIAL_LABEL[s.platform]}`}
										>
											{SOCIAL_ICON[s.platform]}
										</a>
									)
								))
							) : (
								DEFAULT_SOCIAL_LINKS.map((item) => {
									const iconNode = SOCIAL_ICON[item.platform];
									const label = SOCIAL_LABEL[item.platform];
									return linksDisabled ? (
										<span key={item.url} className="text-gray-500" aria-hidden="true">{iconNode}</span>
									) : (
										<a
											key={item.url}
											href={item.url}
											target="_blank"
											rel="noopener noreferrer"
											className="text-gray-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded"
											aria-label={`${effectiveBrandName} on ${label}`}
										>
											{iconNode}
										</a>
									);
								})
							)}
						</div>
					</div>

					{/* Dynamic CMS footer columns */}
					{footerColumns.length > 0 ? (
						footerColumns.map(({ group, items }) => (
							<div key={group.id}>
								<h3 className="font-bold text-white mb-3">{group.name}</h3>
								<ul className="space-y-2 text-sm">
									{items.map((item) => {
										const rawHref = item.slug || item.url || '#';
										const href = resolveDonateNavHref(item.label, rawHref, item.id);
										const isExternal = !!(item.url && !item.slug);
										return (
											<li key={item.id}>
												{item.type === 'text' ? (
													<p className="text-gray-400 text-sm leading-relaxed flex items-start gap-1.5">
														{item.icon && <NavIconRenderer name={item.icon} size={13} className="opacity-50 shrink-0 mt-0.5" />}
														{item.label}
													</p>
												) : item.type === 'html' && item.embedCode ? (
													<div
														className="text-gray-400 text-sm leading-relaxed [&_a]:text-gray-400 [&_a:hover]:text-white [&_a]:transition-colors"
														dangerouslySetInnerHTML={{
															__html: placeholderReplacements
																? replaceCmsPlaceholders(item.embedCode, placeholderReplacements)
																: item.embedCode,
														}}
													/>
												) : linksDisabled ? (
													<span className="text-gray-400 flex items-center gap-1.5 cursor-default">
														{item.icon && <NavIconRenderer name={item.icon} size={13} className="opacity-50 shrink-0" />}
														{item.label}
													</span>
												) : isExternal ? (
													<a href={href} target={item.target ?? '_self'} rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
														{item.icon && <NavIconRenderer name={item.icon} size={13} className="opacity-60 shrink-0" />}
														{item.label}
														{item.target === '_blank' && <ExternalLink size={12} className="opacity-50" />}
													</a>
												) : (
													<Link to={href} className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
														{item.icon && <NavIconRenderer name={item.icon} size={13} className="opacity-60 shrink-0" />}
														{item.label}
													</Link>
												)}
											</li>
										);
									})}
								</ul>
							</div>
						))
					) : (
						/* Hardcoded fallback columns */
						<>
						<div>
						<h3 className="font-bold text-[#D5B446] mb-3 uppercase tracking-[0.14em] text-sm">Programs</h3>
						<ul className="space-y-2 text-sm">
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">Dialysis Support</span>
								) : (
									<Link to="/programs/dialysis-support" className="text-gray-400 hover:text-white transition-colors">Dialysis Support</Link>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">Transplant Fund</span>
								) : (
									<Link to="/programs/transplant-fund" className="text-gray-400 hover:text-white transition-colors">Transplant Fund</Link>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">Medication Aid</span>
								) : (
									<Link to="/programs/medication-aid" className="text-gray-400 hover:text-white transition-colors">Medication Aid</Link>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">CKD Cooperative</span>
								) : (
									<Link to="/programs/ckd-cooperative" className="text-gray-400 hover:text-white transition-colors">CKD Cooperative</Link>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">CKD Warriors</span>
								) : (
									<Link to="/programs/ckd-warriors" className="text-gray-400 hover:text-white transition-colors">CKD Warriors</Link>
								)}
							</li>
						</ul>
					</div>
					<div>
						<h3 className="font-bold text-[#D5B446] mb-3 uppercase tracking-[0.14em] text-sm">Get Involved</h3>
						<ul className="space-y-2 text-sm">
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">Donate Monthly</span>
								) : (
									<Link to="/donate#monthly" className="text-gray-400 hover:text-white transition-colors">Donate Monthly</Link>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">One-Time Gift</span>
								) : (
									<Link to="/donate#one-time" className="text-gray-400 hover:text-white transition-colors">One-Time Gift</Link>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">Corporate Partners</span>
								) : (
									<Link to="/partners" className="text-gray-400 hover:text-white transition-colors">Corporate Partners</Link>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">Ambassador Program</span>
								) : (
									<Link to="/ambassador-program" className="text-gray-400 hover:text-white transition-colors">Ambassador Program</Link>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">Contact Us</span>
								) : (
									<Link to="/contact-us" className="text-gray-400 hover:text-white transition-colors">Contact Us</Link>
								)}
							</li>
						</ul>
					</div>
					<div>
						<h3 className="font-bold text-[#D5B446] mb-3 uppercase tracking-[0.14em] text-sm">Trust & Legal</h3>
						<ul className="space-y-2 text-sm">
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">Privacy Policy</span>
								) : (
									<Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">Terms of Service</span>
								) : (
									<Link to="/terms-of-service" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">Cookie Settings</span>
								) : (
									<button
										type="button"
										onClick={openPreferences}
										className="text-gray-400 hover:text-white transition-colors"
									>
										Cookie Settings
									</button>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">Financial Transparency</span>
								) : (
									<Link to="/financial-transparency" className="text-gray-400 hover:text-white transition-colors">Financial Transparency</Link>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 cursor-default">Press / Media</span>
								) : (
									<Link to="/press-media" className="text-gray-400 hover:text-white transition-colors">Press / Media</Link>
								)}
							</li>
						</ul>
					</div>
					</>
					)}
				</div>
				{!linksDisabled ? (
					<div className="mt-4 flex justify-center lg:justify-end">
						<button
							type="button"
							onClick={openPreferences}
							className="text-xs uppercase tracking-wide text-gray-400 hover:text-white transition-colors"
						>
							Cookie Settings
						</button>
					</div>
				) : null}
				<div className="border-t border-gray-700 mt-6 pt-4 pb-0 text-xs text-gray-500 space-y-2">
					<p className="text-center lg:text-left">{copyrightText}</p>
					<div className="flex items-center justify-center lg:justify-between gap-3 flex-wrap">
						<p className="text-center lg:text-left">
							Good Samaritan Global Outreach is a registered nonprofit organization in the State of Texas, USA. EIN: [Pending]. Philippine affiliate: GSGO Philippines.
						</p>
						<div className="inline-flex items-center gap-5 text-[11px] tracking-widest uppercase">
							<a href="https://www.facebook.com/gsgoorg" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">FB</a>
							<a href="https://www.instagram.com/gsgoorg" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">IG</a>
							<a href="https://www.youtube.com/@gsgoorg" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">YT</a>
							<a href="https://tiktok.com/@gsgoorg" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">TT</a>
							<a href="https://www.linkedin.com/company/gsgo/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">IN</a>
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
};
